import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'

/**
 * GLB 文件头长度。
 */
const GLB_HEADER_LENGTH = 12

/**
 * GLB chunk 头长度。
 */
const GLB_CHUNK_HEADER_LENGTH = 8

/**
 * GLB magic 文本。
 */
const GLB_MAGIC = 'glTF'

/**
 * JSON chunk 类型常量。
 */
const GLB_JSON_CHUNK_TYPE = 0x4E4F534A

/**
 * 是否打印原始 GLB JSON 调试对象。
 */
const ENABLE_RAW_GLB_JSON_DEBUG_LOG = false

/**
 * GLB 二进制摘要。
 */
interface GlbBinarySummary {
  /**
   * 文件实际字节长度。
   */
  actualByteLength: number
  /**
   * 文件头声明的总长度。
   */
  declaredLength: number
  /**
   * GLB 版本号。
   */
  version: number
  /**
   * JSON chunk 长度。
   */
  jsonChunkLength: number
  /**
   * JSON chunk 在文件中的绝对偏移。
   */
  jsonChunkOffset: number
  /**
   * chunk 数量。
   */
  chunkCount: number
  /**
   * 头部声明之外的尾随字节数。
   */
  trailingByteLength: number
}

/**
 * 原始 chunk 摘要。
 */
interface GlbChunkSummary {
  /**
   * chunk 顺序下标。
   */
  index: number
  /**
   * chunk 类型。
   */
  type: number
  /**
   * chunk 长度。
   */
  length: number
  /**
   * chunk 数据偏移。
   */
  dataOffset: number
  /**
   * 可读类型标签。
   */
  typeLabel: string
}

/**
 * 解析前打印的原始 GLB 调试信息。
 */
interface RawGlbDebugInfo {
  /**
   * 二进制摘要。
   */
  summary: GlbBinarySummary
  /**
   * chunk 列表。
   */
  chunks: GlbChunkSummary[]
  /**
   * 原始 JSON 文本。
   */
  rawJsonText: string
  /**
   * 原始 JSON 对象。
   */
  rawJsonObject: Record<string, unknown> | null
}

/**
 * 原始 GLB 文本解码器。
 */
const rawGlbTextDecoder = new TextDecoder()

/**
 * 格式化字节长度文本。
 *
 * @param value 字节数。
 * @returns 人类可读文本。
 */
function formatByteLength(value: number): string {
  return `${value.toLocaleString('en-US')}B`
}

/**
 * 构建 GLB 摘要文本。
 *
 * @param summary 二进制摘要。
 * @returns 摘要文本。
 */
function buildBinarySummaryText(summary: GlbBinarySummary): string {
  const parts = [
    `文件大小=${formatByteLength(summary.actualByteLength)}`,
    `头部声明=${formatByteLength(summary.declaredLength)}`,
    `JSON块=${formatByteLength(summary.jsonChunkLength)}`,
    `JSON偏移=${formatByteLength(summary.jsonChunkOffset)}`,
    `chunk数=${summary.chunkCount}`,
    `版本=${summary.version}`,
  ]

  if (summary.trailingByteLength > 0) {
    parts.push(`尾随字节=${formatByteLength(summary.trailingByteLength)}`)
  }

  return parts.join('，')
}

/**
 * 构建统一的 GLB 预检错误。
 *
 * @param message 错误详情。
 * @param options 原始异常等标准错误选项。
 * @returns 统一错误对象。
 */
function createGlbBinaryError(message: string, options?: ErrorOptions): Error {
  return new Error(`[GLB预检失败] ${message}`, options)
}

/**
 * 将 chunk 类型转换为可读标签。
 *
 * @param chunkType chunk 类型值。
 * @returns 标签文本。
 */
function resolveChunkTypeLabel(chunkType: number): string {
  if (chunkType === GLB_JSON_CHUNK_TYPE) {
    return 'JSON'
  }

  if (chunkType === 0x004E4942) {
    return 'BIN'
  }

  return `0x${chunkType.toString(16).toUpperCase().padStart(8, '0')}`
}

/**
 * 预检 GLB 二进制结构。
 *
 * 设计说明：
 * 1. 在进入 `GLTFLoader.parse()` 之前尽早暴露文件截断、长度错位、缺 JSON chunk 等底层问题；
 * 2. 这一步只做结构校验，不做业务语义解析，避免把导入协议逻辑塞进底层字节检查；
 * 3. 预检产出的摘要会同时用于错误包装和原始 JSON 调试打印。
 *
 * @param data 原始 GLB 字节流。
 * @returns 二进制摘要。
 */
function inspectGlbBinary(data: Uint8Array): GlbBinarySummary {
  if (data.byteLength < GLB_HEADER_LENGTH) {
    throw createGlbBinaryError(
      `文件头长度不足，至少需要 ${GLB_HEADER_LENGTH}B，实际只有 ${formatByteLength(data.byteLength)}`,
    )
  }

  const headerView = new DataView(data.buffer, data.byteOffset, GLB_HEADER_LENGTH)
  const magic = rawGlbTextDecoder.decode(data.subarray(0, 4))
  if (magic !== GLB_MAGIC) {
    throw createGlbBinaryError(`文件头 magic 不是 ${GLB_MAGIC}，当前为 ${JSON.stringify(magic)}`)
  }

  const version = headerView.getUint32(4, true)
  if (version < 2) {
    throw createGlbBinaryError(`仅支持 glTF 2.0 二进制格式，当前版本为 ${version}`)
  }

  const declaredLength = headerView.getUint32(8, true)
  if (declaredLength < GLB_HEADER_LENGTH) {
    throw createGlbBinaryError(
      `头部声明长度非法：${formatByteLength(declaredLength)}，小于文件头长度 ${GLB_HEADER_LENGTH}B`,
    )
  }

  if (declaredLength > data.byteLength) {
    throw createGlbBinaryError(
      `文件内容已截断：头部声明总长度 ${formatByteLength(declaredLength)}，实际仅有 ${formatByteLength(data.byteLength)}`,
    )
  }

  const chunkViewLength = declaredLength - GLB_HEADER_LENGTH
  const chunkView = new DataView(data.buffer, data.byteOffset + GLB_HEADER_LENGTH, chunkViewLength)
  let cursor = 0
  let chunkCount = 0
  let jsonChunkLength = -1
  let jsonChunkOffset = -1

  while (cursor < chunkViewLength) {
    if (cursor + GLB_CHUNK_HEADER_LENGTH > chunkViewLength) {
      throw createGlbBinaryError(
        `chunk 头不完整：偏移 ${formatByteLength(GLB_HEADER_LENGTH + cursor)} 处剩余字节不足 ${GLB_CHUNK_HEADER_LENGTH}B`,
      )
    }

    const chunkLength = chunkView.getUint32(cursor, true)
    cursor += 4
    const chunkType = chunkView.getUint32(cursor, true)
    cursor += 4
    chunkCount += 1

    const chunkDataStart = GLB_HEADER_LENGTH + cursor
    const chunkDataEnd = chunkDataStart + chunkLength
    if (chunkDataEnd > declaredLength || chunkDataEnd > data.byteLength) {
      throw createGlbBinaryError(
        `chunk 数据不完整：第 ${chunkCount} 个 chunk 声明长度 ${formatByteLength(chunkLength)}，但文件在 ${formatByteLength(chunkDataStart)} 之后已不足`,
      )
    }

    if (chunkType === GLB_JSON_CHUNK_TYPE && jsonChunkLength < 0) {
      jsonChunkLength = chunkLength
      jsonChunkOffset = chunkDataStart
    }

    cursor += chunkLength
  }

  if (jsonChunkLength < 0 || jsonChunkOffset < 0) {
    throw createGlbBinaryError('未找到 JSON chunk，文件不是合法的 GLB 结构')
  }

  return {
    actualByteLength: data.byteLength,
    declaredLength,
    version,
    jsonChunkLength,
    jsonChunkOffset,
    chunkCount,
    trailingByteLength: data.byteLength - declaredLength,
  }
}

/**
 * 读取 GLB 的原始 JSON chunk，而不创建任何 Three.js 运行时资源。
 *
 * 该入口与正式 `parseGlbBinary` 复用相同的二进制结构预检，供 complete GLB 在正式
 * GLTFLoader 实例化前完成根选择、occurrence coverage 和协议校验。
 *
 * @param data 原始 GLB 字节流。
 * @returns 保留原始 nodes 索引的 glTF JSON 文档。
 */
export function parseGlbJsonDocument(data: Uint8Array): PuppyCAD.CompleteGlbJsonDocument {
  const summary = inspectGlbBinary(data)
  const rawJsonBytes = data.subarray(
    summary.jsonChunkOffset,
    summary.jsonChunkOffset + summary.jsonChunkLength,
  )
  const rawJsonText = rawGlbTextDecoder.decode(rawJsonBytes).replace(/\u0000+$/g, '').trimEnd()
  let parsed: unknown

  try {
    parsed = JSON.parse(rawJsonText)
  } catch (error) {
    throw createGlbBinaryError('JSON chunk 解析失败', { cause: error })
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw createGlbBinaryError('JSON 根必须是对象')
  }
  return parsed as PuppyCAD.CompleteGlbJsonDocument
}

/**
 * 构建解析前的原始 GLB 调试信息。
 *
 * @param data 原始 GLB 字节流。
 * @param summary 二进制摘要。
 * @returns 原始调试信息。
 */
function buildRawGlbDebugInfo(data: Uint8Array, summary: GlbBinarySummary): RawGlbDebugInfo {
  const chunkViewLength = summary.declaredLength - GLB_HEADER_LENGTH
  const chunkView = new DataView(
    data.buffer,
    data.byteOffset + GLB_HEADER_LENGTH,
    chunkViewLength,
  )

  const chunks: GlbChunkSummary[] = []
  let cursor = 0

  while (cursor < chunkViewLength) {
    const chunkLength = chunkView.getUint32(cursor, true)
    cursor += 4
    const chunkType = chunkView.getUint32(cursor, true)
    cursor += 4

    const dataOffset = GLB_HEADER_LENGTH + cursor
    chunks.push({
      index: chunks.length,
      type: chunkType,
      length: chunkLength,
      dataOffset,
      typeLabel: resolveChunkTypeLabel(chunkType),
    })

    cursor += chunkLength
  }

  const rawJsonBytes = data.subarray(
    summary.jsonChunkOffset,
    summary.jsonChunkOffset + summary.jsonChunkLength,
  )
  const rawJsonText = rawGlbTextDecoder.decode(rawJsonBytes).replace(/\u0000+$/g, '').trimEnd()

  let rawJsonObject: Record<string, unknown> | null = null
  try {
    const parsedJson = JSON.parse(rawJsonText) as unknown
    rawJsonObject = typeof parsedJson === 'object' && parsedJson !== null
      ? parsedJson as Record<string, unknown>
      : null
  } catch {
    rawJsonObject = null
  }

  return {
    summary,
    chunks,
    rawJsonText,
    rawJsonObject,
  }
}

/**
 * 打印原始 GLB JSON 调试信息。
 *
 * 设计说明：
 * 1. 该能力只用于临时排查 GLB 导出协议，默认必须关闭；
 * 2. 原始 JSON 对象可能包含数万 accessors，默认打印会造成重复解析与控制台对象保留；
 * 3. 需要排查时只允许通过本文件顶部调试开关显式打开。
 *
 * @param data 原始 GLB 字节。
 * @param summary GLB 二进制摘要。
 */
function printRawGlbDebugInfo(data: Uint8Array, summary: GlbBinarySummary): void {
  // 原始 GLB JSON 对象通常很大，默认打印会重复 JSON.parse 并拖慢大装配加载。
  if (!ENABLE_RAW_GLB_JSON_DEBUG_LOG) {
    return
  }

  const debugInfo = buildRawGlbDebugInfo(data, summary)
  console.info('[GLB原始JSON对象]', debugInfo.rawJsonObject)
}

/**
 * 将字节流转换为 `GLTFLoader.parse()` 可消费的 ArrayBuffer。
 *
 * @param data 原始字节流。
 * @returns 连续 ArrayBuffer。
 */
function resolveParseBuffer(data: Uint8Array): ArrayBuffer {
  if (data.byteOffset === 0 && data.byteLength === data.buffer.byteLength) {
    return data.buffer as ArrayBuffer
  }

  return data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer
}

/**
 * 包装 `GLTFLoader.parse()` 抛出的错误。
 *
 * @param error 原始错误。
 * @param summary 二进制摘要。
 * @returns 统一错误对象。
 */
function wrapGlbParseError(error: unknown, summary: GlbBinarySummary): Error {
  const detail = error instanceof Error ? error.message : String(error)
  const summaryText = buildBinarySummaryText(summary)

  if (/Unexpected end of JSON input/i.test(detail)) {
    return new Error(
      `[GLB解析失败] GLB 内嵌 JSON 块解析未完成，通常表示文件已截断或 JSON chunk 声明长度异常。${summaryText}`,
    )
  }

  return new Error(`[GLB解析失败] ${detail}。${summaryText}`)
}

/**
 * 解析 GLB 文件。
 *
 * @param loader GLTFLoader 实例。
 * @param data 原始 GLB 字节流。
 * @returns 解析后的 GLTF。
 */
export function parseGlbBinary(loader: GLTFLoader, data: Uint8Array): Promise<GLTF> {
  let summary: GlbBinarySummary

  try {
    summary = inspectGlbBinary(data)
  } catch (error) {
    return Promise.reject(error)
  }

  printRawGlbDebugInfo(data, summary)
  const parseBuffer = resolveParseBuffer(data)

  return new Promise((resolve, reject) => {
    try {
      loader.parse(
        parseBuffer,
        '',
        (gltf) => resolve(gltf),
        (error) => reject(wrapGlbParseError(error, summary)),
      )
    } catch (error) {
      reject(wrapGlbParseError(error, summary))
    }
  })
}
